import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppStore, RequestStatus, userActions } from '@akademiasaas/shared';
import * as styles from './VerifyEmail.module.scss';
import { auth, useAppDispatch } from '~/initializeStore';
import { useQuery } from '~/hooks/useQuery';
import { getSafeAuthRedirectPath } from '~/pages/Auth/helpers/getAuthRedirectPath';

const RESEND_COOLDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;

const getRemainingCooldownSeconds = (sentAt: Date | null | undefined) => {
  if (!sentAt) {
    return 0;
  }

  return Math.max(
    0,
    Math.ceil((sentAt.getTime() + RESEND_COOLDOWN_SECONDS * 1000 - Date.now()) / 1000)
  );
};

const getAccountCreationDate = () => {
  const creationTime = auth.currentUser?.metadata.creationTime;

  return creationTime ? new Date(creationTime) : null;
};

export const VerifyEmail: FunctionComponent = () => {
  const { t } = useTranslation(['auth', 'common']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useQuery();
  const continuePath = getSafeAuthRedirectPath(query.get('continue')) || '/';
  const { sendVerificationEmailStatus, sendVerificationEmailError, details, detailsStatus } =
    useSelector((store: AppStore) => store.user);
  const [cooldown, setCooldown] = useState(() =>
    getRemainingCooldownSeconds(getAccountCreationDate())
  );
  const [notVerifiedYet, setNotVerifiedYet] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const email = auth.currentUser?.email;
  // Guards the poll against navigating after the component has gone away.
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const checkVerified = useCallback(async () => {
    const verified = await dispatch(userActions.refreshEmailVerified());

    if (verified && isMounted.current) {
      history.push(continuePath);
    }

    return verified;
  }, [continuePath, dispatch, history]);

  // Advance on its own when the user confirms in another tab.
  useEffect(() => {
    const interval = setInterval(checkVerified, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [checkVerified]);

  useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }

    const timeout = setTimeout(() => setCooldown((value) => value - 1), 1000);

    return () => clearTimeout(timeout);
  }, [cooldown]);

  useEffect(() => {
    // A newly-created account is protected from the moment its document is
    // written. Once the automatic email finishes, use the backend timestamp so
    // the visible countdown follows the same one-minute window as the server.
    const sentAt = details?.lastVerificationEmailSentAt || details?.createdAt;
    const remaining = getRemainingCooldownSeconds(sentAt);

    setCooldown((current) => Math.max(current, remaining));
  }, [details?.createdAt, details?.lastVerificationEmailSentAt]);

  const handleResend = () => {
    setNotVerifiedYet(false);
    dispatch(
      userActions.resendVerificationEmail(new URL(continuePath, window.location.origin).toString())
    );
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const handleCheckAgain = async () => {
    setIsChecking(true);
    const verified = await checkVerified();

    if (isMounted.current) {
      setNotVerifiedYet(!verified);
      setIsChecking(false);
    }
  };

  const handleLogOut = () => {
    dispatch(userActions.logOutUser(() => history.push('/auth/login')));
  };

  const resendFeedback = (() => {
    if (sendVerificationEmailStatus === RequestStatus.SUCCESS) {
      return { type: 'success' as const, message: t<string>('verifyEmail.resendSuccess') };
    }

    if (sendVerificationEmailStatus === RequestStatus.FAILED) {
      return {
        type: 'error' as const,
        message:
          sendVerificationEmailError === 'functions/resource-exhausted'
            ? t<string>('verifyEmail.tooManyRequests')
            : t<string>('verifyEmail.resendError'),
      };
    }

    return null;
  })();

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <Typography.Title level={4} className={styles.header}>
          {t<string>('verifyEmail.title')}
        </Typography.Title>

        <Typography.Text className={styles.subtitle}>
          {t<string>('verifyEmail.subtitle')}
        </Typography.Text>

        {email && (
          <Typography.Text className={styles.sentTo}>
            <Trans i18nKey="auth:verifyEmail.sentTo" values={{ email }}>
              <strong />
            </Trans>
          </Typography.Text>
        )}

        <Typography.Text type="secondary" className={styles.spamHint}>
          {t<string>('verifyEmail.spamHint')}
        </Typography.Text>

        {resendFeedback && (
          <Alert
            className={styles.alert}
            type={resendFeedback.type}
            title={resendFeedback.message}
          />
        )}

        {notVerifiedYet && !resendFeedback && (
          <Alert
            className={styles.alert}
            type="warning"
            title={t<string>('verifyEmail.stillNotVerified')}
          />
        )}

        <div className={styles.actions}>
          <Button type="primary" block onClick={handleCheckAgain} loading={isChecking}>
            {t<string>('verifyEmail.checkAgain')}
          </Button>

          <Button
            block
            onClick={handleResend}
            disabled={cooldown > 0 || detailsStatus !== RequestStatus.SUCCESS}
            loading={sendVerificationEmailStatus === RequestStatus.FETCHING}
          >
            {cooldown > 0
              ? t<string>('verifyEmail.resendCooldown', { seconds: cooldown })
              : t<string>('verifyEmail.resend')}
          </Button>

          <Button type="link" onClick={handleLogOut}>
            {t<string>('verifyEmail.logOut')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
